import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { OwnerDashboardSummary, OwnerEvent, clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';
import {
  AdminDataTable,
  AdminSection,
  adminTableCellClassName,
  adminTableClassName,
  adminTableHeadCellClassName,
} from '@/components/admin/AdminLayout';
import AdminShell from '@/components/admin/AdminShell';
import TablePagination from '@/components/common/TablePagination';
import { CheckCircle2, Circle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useStripeConnectStatus } from '@/contexts/StripeConnectStatusContext';

type RevenueBucket = {
  key: string;
  label: string;
  revenueCents: number;
};

const Dashboard = () => {
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardSummary, setDashboardSummary] = useState<OwnerDashboardSummary | null>(null);
  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [includeCashRevenue, setIncludeCashRevenue] = useState(true);
  const [gettingStartedExpanded, setGettingStartedExpanded] = useState(true);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize, setEventsPageSize] = useState(7);
  const { isStripeReady: stripeConnected, loading: stripeStatusLoading } = useStripeConnectStatus();

  const ownerName = auth.getUser().name || 'Club owner';

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }),
    [],
  );

  const stripeRevenueCents = dashboardSummary?.totals.stripeRevenueCents || 0;
  const paidCashRevenueCents = dashboardSummary?.totals.paidCashRevenueCents || 0;

  const displayedRevenueCents = includeCashRevenue
    ? stripeRevenueCents + paidCashRevenueCents
    : stripeRevenueCents;

  const revenueByMonth = useMemo<RevenueBucket[]>(() => {
    const summaryBuckets = dashboardSummary?.revenueByMonth || [];
    if (!summaryBuckets.length) {
      const formatter = new Intl.DateTimeFormat('en-GB', { month: 'short' });
      const now = new Date();
      return Array.from({ length: 6 }).map((_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return {
          key,
          label: formatter.format(date),
          revenueCents: 0,
        };
      });
    }

    return summaryBuckets.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      revenueCents: includeCashRevenue
        ? Number(bucket.stripeRevenueCents || 0) + Number(bucket.cashRevenueCents || 0)
        : Number(bucket.stripeRevenueCents || 0),
    }));
  }, [dashboardSummary, includeCashRevenue]);

  const maxRevenueCents = useMemo(
    () => Math.max(1, ...revenueByMonth.map((bucket) => bucket.revenueCents)),
    [revenueByMonth],
  );

  const linePoints = useMemo(() => {
    if (revenueByMonth.length === 0) return '';
    if (revenueByMonth.length === 1) {
      const y = 100 - (revenueByMonth[0].revenueCents / maxRevenueCents) * 100;
      return `50,${y}`;
    }

    return revenueByMonth
      .map((bucket, index) => {
        const x = (index / (revenueByMonth.length - 1)) * 100;
        const y = 100 - (bucket.revenueCents / maxRevenueCents) * 100;
        return `${x},${y}`;
      })
      .join(' ');
  }, [maxRevenueCents, revenueByMonth]);

  const areaPoints = useMemo(() => {
    if (!linePoints) return '';
    return `0,100 ${linePoints} 100,100`;
  }, [linePoints]);

  const attendanceSnapshot = useMemo(() => {
    return {
      sessionsBooked: Number(dashboardSummary?.totals.sessionsBooked || 0),
      sessionsAttended: Number(dashboardSummary?.totals.sessionsAttended || 0),
    };
  }, [dashboardSummary?.totals.sessionsAttended, dashboardSummary?.totals.sessionsBooked]);

  const hasCreatedActivity = Number(dashboardSummary?.totals.activities || 0) > 0;
  const hasReceivedBooking = Number(dashboardSummary?.totals.bookings || 0) > 0;

  const gettingStartedSteps = useMemo(
    () => [
      {
        id: 'activity',
        title: 'Set up your first activity',
        description: 'Create a published activity with session dates so parents can start booking.',
        done: hasCreatedActivity,
        to: hasCreatedActivity ? '/activities' : '/activities/new',
        actionLabel: hasCreatedActivity ? 'View activities' : 'Create activity',
      },
      {
        id: 'stripe',
        title: 'Connect Stripe to take online payments',
        description: 'Link your Stripe account so card payments are ready when parents check out.',
        done: !stripeStatusLoading && stripeConnected,
        to: '/bookings',
        actionLabel: stripeStatusLoading ? 'Checking...' : stripeConnected ? 'Manage Stripe' : 'Connect Stripe',
      },
      {
        id: 'booking',
        title: 'Send your booking link and get a booking',
        description: 'Share your booking page with a parent and confirm your first booking lands successfully.',
        done: hasReceivedBooking,
        to: hasCreatedActivity ? '/activities' : '/activities/new',
        actionLabel: hasReceivedBooking ? 'View bookings' : hasCreatedActivity ? 'Copy booking link' : 'Create activity first',
      },
    ],
    [hasCreatedActivity, hasReceivedBooking, stripeConnected, stripeStatusLoading],
  );

  const completedGettingStartedSteps = gettingStartedSteps.filter((step) => step.done).length;
  const hasOutstandingGettingStartedSteps = completedGettingStartedSteps < gettingStartedSteps.length;
  const gettingStartedVisibilityReady = !summaryLoading && !stripeStatusLoading && !!dashboardSummary;

  useEffect(() => {
    const loadSummary = async () => {
      setSummaryLoading(true);
      setError('');
      try {
        const summary = await clubsBookingApi.getOwnerDashboardSummary();
        setDashboardSummary(summary);
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load dashboard');
      } finally {
        setSummaryLoading(false);
      }
    };

    void loadSummary();
  }, []);

  useEffect(() => {
    const loadEventsPage = async () => {
      setEventsLoading(true);
      setError('');
      try {
        const eventsData = await clubsBookingApi.listMyEvents(undefined, {
          page: eventsPage,
          limit: eventsPageSize,
        });
        setEvents(eventsData.items || []);
        setEventsTotal(Number(eventsData.total || 0));
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load dashboard activities');
      } finally {
        setEventsLoading(false);
      }
    };

    void loadEventsPage();
  }, [eventsPage, eventsPageSize]);

  return (
    <AdminShell title="Dashboard" subtitle={`Welcome back, ${ownerName}`}>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <Link
        to="/billing"
        className="mb-4 block rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 sm:mb-6"
      >
        <span className="font-semibold">Get paid promoting Session Monkey!</span>{' '}
        Use your referral code and get 50% of lifetime monthly payments from people who use your link!
        <span className="ml-2 inline-flex font-semibold text-emerald-700">View referrals <ArrowRight className="ml-1 h-4 w-4" /></span>
      </Link>

      {gettingStartedVisibilityReady && hasOutstandingGettingStartedSteps && (
        <AdminSection
          className="mb-4 shadow-sm sm:mb-6"
          data-testid="getting-started"
        >
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold md:text-2xl">Getting started</h2>
              <p className="mt-1 text-sm text-slate-600">
                Start here to get your club live and taking bookings quickly.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <p
                className={`rounded-full px-3 py-1 text-sm font-semibold ${
                  hasOutstandingGettingStartedSteps ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {completedGettingStartedSteps}/{gettingStartedSteps.length} completed
              </p>
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                aria-label={gettingStartedExpanded ? 'Collapse getting started' : 'Expand getting started'}
                onClick={() => setGettingStartedExpanded((value) => !value)}
              >
                {gettingStartedExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {gettingStartedExpanded && (
            <div className="grid gap-4 md:grid-cols-3">
              {gettingStartedSteps.map((step, index) => (
                <article
                  key={step.id}
                  className={`rounded-xl border p-3.5 md:p-4 ${step.done ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}
                  data-testid={`getting-started-step-${step.id}`}
                  data-status={step.done ? 'complete' : 'todo'}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-500">Step {index + 1}</p>
                    <div className={step.done ? 'mt-0.5 text-emerald-600' : 'mt-0.5 text-amber-600'}>
                      {step.done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                    </div>
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 md:min-h-[58px]">{step.description}</p>

                  <div className="mt-3 flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${step.done ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {step.done ? 'Completed' : step.id === 'stripe' && stripeStatusLoading ? 'Checking...' : 'To do'}
                    </span>
                    <Link
                      to={step.done && step.id === 'booking' ? '/bookings' : step.to}
                      className={`inline-flex items-center gap-1 text-sm font-semibold hover:underline ${
                        step.done ? 'text-emerald-700' : 'text-amber-700'
                      }`}
                    >
                      {step.actionLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </AdminSection>
      )}

      <section className="mb-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold md:text-3xl">Business performance</h2>
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[#1F8F5F]"
              checked={includeCashRevenue}
              onChange={(e) => setIncludeCashRevenue(e.target.checked)}
              data-testid="dashboard-include-cash-toggle"
            />
            Include cash payments
          </label>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="grid md:grid-cols-3">
            <div className="border-b border-slate-200 bg-[#ECF8F3] p-4 md:border-b-0 md:border-r md:p-6">
              <p className="text-base text-slate-600 md:text-lg">Revenue</p>
              <p
                className="mt-1 text-3xl font-semibold text-[#1F8F5F] md:text-4xl"
                data-testid="dashboard-revenue-value"
                data-revenue-cents={displayedRevenueCents}
              >
                {(summaryLoading || eventsLoading) ? 'Loading...' : currencyFormatter.format(displayedRevenueCents / 100)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {includeCashRevenue ? 'Online card + cash payments' : 'Online card payments only'}
              </p>
            </div>
            <div className="border-b border-slate-200 p-4 md:border-b-0 md:border-r md:p-6">
              <p className="text-base text-slate-600 md:text-lg">Bookings</p>
              <Link to="/bookings" className="mt-1 block text-3xl font-semibold text-[#1F8F5F] hover:underline md:text-4xl">
                {Number(dashboardSummary?.totals.bookings || 0)}
              </Link>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-base text-slate-600 md:text-lg">Activities</p>
              <Link to="/activities" className="mt-1 block text-3xl font-semibold text-[#1F8F5F] hover:underline md:text-4xl">
                {Number(dashboardSummary?.totals.activities || 0)}
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-5">
            <p className="text-sm uppercase tracking-wide text-slate-500">Session attendance</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{attendanceSnapshot.sessionsAttended}</p>
            <p className="mt-1 text-sm text-slate-500">Sessions attended (checked in/out)</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-5">
            <p className="text-sm uppercase tracking-wide text-slate-500">Session bookings</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{attendanceSnapshot.sessionsBooked}</p>
            <p className="mt-1 text-sm text-slate-500">Session places booked</p>
          </div>
        </div>
      </section>

      <AdminSection>
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold md:text-2xl">Revenue trend</h2>
          <p className="text-sm text-slate-500">
            Last 6 months {includeCashRevenue ? '(including cash)' : '(online card only)'}
          </p>
        </div>

        <div className="rounded-xl border border-slate-100 bg-gradient-to-b from-[#F6FAF7] to-white p-3 sm:p-4">
          <div className="relative h-64 w-full">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
              <line x1="0" y1="25" x2="100" y2="25" stroke="#e2e8f0" strokeWidth="0.6" />
              <line x1="0" y1="50" x2="100" y2="50" stroke="#e2e8f0" strokeWidth="0.6" />
              <line x1="0" y1="75" x2="100" y2="75" stroke="#e2e8f0" strokeWidth="0.6" />
              {areaPoints && <polygon points={areaPoints} fill="#1F8F5F" fillOpacity="0.12" />}
              {linePoints && <polyline points={linePoints} fill="none" stroke="#1F8F5F" strokeWidth="1.2" />}
            </svg>
          </div>

          <div className="mt-3 grid grid-cols-6 gap-2 text-xs text-slate-500">
            {revenueByMonth.map((bucket) => (
              <div
                key={bucket.key}
                className="text-center"
                data-testid={`dashboard-revenue-bucket-${bucket.key}`}
                data-revenue-cents={bucket.revenueCents}
              >
                <div className="font-medium text-slate-600">{bucket.label}</div>
                <div>{currencyFormatter.format(bucket.revenueCents / 100)}</div>
              </div>
            ))}
          </div>
        </div>
      </AdminSection>

      <AdminSection className="mt-6">
        <h2 className="text-xl font-semibold md:text-2xl">Activities overview</h2>
        <AdminDataTable className="mt-4">
          <table className={`${adminTableClassName} min-w-[640px]`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className={adminTableHeadCellClassName}>Activity</th>
                <th className={adminTableHeadCellClassName}>Status</th>
                <th className={adminTableHeadCellClassName}>Booked spots</th>
                <th className={adminTableHeadCellClassName}>Attendees</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((event) => (
                <tr key={event._id}>
                  <td className={`${adminTableCellClassName} font-medium`}>
                    <Link to={`/activities/${event._id}`} className="text-[#1F8F5F] hover:underline">
                      {event.title}
                    </Link>
                  </td>
                  <td className={`${adminTableCellClassName} capitalize`}>{event.status === 'published' ? 'Live' : 'Not live'}</td>
                  <td className={adminTableCellClassName}>{event.bookedSpots}</td>
                  <td className={adminTableCellClassName}>
                    <Link to={`/attendees?eventId=${event._id}`} className="text-[#1F8F5F] hover:underline">
                      View attendees
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!eventsLoading && eventsTotal === 0 && <p className="px-4 py-6 text-sm text-slate-500">No activities found.</p>}
          {!eventsLoading && eventsTotal > 0 && (
            <TablePagination
              totalItems={eventsTotal}
              currentPage={eventsPage}
              pageSize={eventsPageSize}
              onPageChange={setEventsPage}
              onPageSizeChange={(size) => {
                setEventsPageSize(size);
                setEventsPage(1);
              }}
            />
          )}
        </AdminDataTable>
      </AdminSection>
    </AdminShell>
  );
};

export default Dashboard;



